import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';
import { SITE_ORIGIN } from '@/site';
import { BLOG_POSTS } from '@/components/data/blogPosts';
import { BlogLayout } from './BlogLayout';

export const BlogPage = () => {
  useSEO({
    title: 'Playano Blog - AI-Native Studio, Products, and Engineering | Playano Education',
    description:
      'How Playano builds AI-native products: the studio methodology, the product portfolio, the reproducible engineering benchmarks, and the AI supply chain behind them.',
    keywords:
      'AI-native studio, Playano products, RAG benchmark, LLM engineering, AI stack dependence, Playano Education blog',
    ogTitle: 'Playano Blog',
    ogDescription: 'The studio methodology, the product portfolio, and the engineering behind Playano.',
    canonicalUrl: `${SITE_ORIGIN}/blog`,
  });

  return (
    <BlogLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20">
        <div className="max-w-3xl mb-12">
          <span className="inline-block mb-4 px-3 py-1 rounded-full bg-[#FD9800]/10 border border-[#FD9800]/30 text-[#FD9800] text-xs font-semibold uppercase tracking-wide">
            Playano Studio
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            One studio. Eight products. One method.
          </h1>
          <p className="text-lg text-gray-400">
            How Playano builds AI-native products &mdash; the studio methodology, the product portfolio, the
            reproducible engineering behind them, and the supply chain they run on.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {BLOG_POSTS.map((post) => (
            <Link
              key={post.slug}
              to={`/${post.slug}`}
              className="group bg-[#171717] rounded-xl border border-[#333] hover:border-[#FD9800] transition-all overflow-hidden"
            >
              <div className="overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-44 object-cover group-hover:scale-[1.03] transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold uppercase tracking-wide text-[#FD9800]">
                    {post.category}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(post.date + 'T00:00:00Z').toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      timeZone: 'UTC',
                    })}
                  </span>
                </div>
                <h2 className="text-lg font-semibold text-white group-hover:text-[#FD9800] transition-colors">
                  {post.title}
                </h2>
                <p className="mt-2 text-sm text-gray-400">{post.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#FD9800]">
                  Read <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </BlogLayout>
  );
};
